/**
 * Multi Image Upload Renderer
 *
 * Allows uploading multiple images with preview, drag & drop, and remove functionality
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';
import type { FormField } from '@/MyCode/components/JsonFormSteps';
import { cn } from '@/lib/utils';
import { useFileUpload } from '@/MyCode/hooks/use-file-upload';

/**
 * Separate component for MultiImageUpload to properly handle hooks
 */
const MultiImageUploadComponent: React.FC<{
    field: FormField;
    value: any;
    handleChange: (name: string, value: any) => void;
    isRequired: boolean;
}> = ({ field, value, handleChange, isRequired }) => {
    const [{ files }, { removeFile, openFileDialog, getInputProps }] = useFileUpload({
        accept: 'image/*',
        multiple: true,
        maxSize: 5 * 1024 * 1024, // 5MB max per file
    });

    const prevFilesLengthRef = React.useRef<number>(0);
    const [isConverting, setIsConverting] = React.useState(false);

    // Convert File to base64
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Sync files with form value when files change
    React.useEffect(() => {
        if (files.length !== prevFilesLengthRef.current) {
            prevFilesLengthRef.current = files.length;

            // Convert all files to base64 strings
            setIsConverting(true);
            Promise.all(files.map(f => fileToBase64(f.file)))
                .then(base64Images => {
                    handleChange(field.name, base64Images);
                    setIsConverting(false);
                })
                .catch(error => {
                    console.error('Error converting files to base64:', error);
                    setIsConverting(false);
                });
        }
    }, [files, field.name, handleChange]);

    const UploadIcon = LucideIcons.Upload;
    const XIcon = LucideIcons.X;
    const ImageIcon = LucideIcons.Image;

    const maxImages = field.maxImages || 10;
    const canAddMore = files.length < maxImages;

    return (
        <div className="space-y-3">
            <Label>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>

            {/* Upload Button */}
            {canAddMore && (
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={openFileDialog}
                        disabled={isConverting}
                        className="w-full sm:w-auto"
                    >
                        <UploadIcon className="w-4 h-4 mr-2" />
                        {isConverting ? 'Processing...' : `Upload Images (${files.length}/${maxImages})`}
                    </Button>
                    <input
                        {...getInputProps()}
                        className="sr-only"
                        aria-label="Upload image files"
                        tabIndex={-1}
                    />
                </div>
            )}

            {/* Image Grid */}
            {files.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {files.map((uploadedFile, index) => (
                        <div
                            key={uploadedFile.id}
                            className="relative group aspect-square rounded-lg overflow-hidden border-2 border-muted hover:border-primary transition-colors"
                        >
                            <img
                                src={uploadedFile.preview}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                            {/* Remove button */}
                            <Button
                                type="button"
                                onClick={() => removeFile(uploadedFile.id)}
                                size="icon"
                                variant="destructive"
                                className="absolute top-2 right-2 size-7 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                aria-label={`Remove image ${index + 1}`}
                            >
                                <XIcon className="size-4" />
                            </Button>
                            {/* Image number badge */}
                            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                {index + 1}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {files.length === 0 && (
                <div
                    onClick={openFileDialog}
                    className={cn(
                        "border-2 border-dashed border-muted rounded-lg p-8 text-center cursor-pointer",
                        "hover:border-primary hover:bg-muted/50 transition-colors"
                    )}
                >
                    <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-1">
                        {field.placeholder || 'Click to upload images or drag and drop'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        PNG, JPG, GIF up to 5MB (max {maxImages} images)
                    </p>
                </div>
            )}

            {/* Helper text */}
            {field.helperText && (
                <p className="text-xs text-muted-foreground">{field.helperText}</p>
            )}
        </div>
    );
};

/**
 * Multi Image Upload Renderer
 * Features:
 * - Multiple image upload support
 * - Image preview grid
 * - Remove individual images
 * - Drag & drop support (via useFileUpload hook)
 * - Maximum file size validation
 * - Maximum number of images limit
 * - Empty state with instructions
 */
export const renderMultiImageUpload: FieldRenderer = ({ field, value, handleChange, isRequired }) => {
    return (
        <MultiImageUploadComponent
            field={field}
            value={value}
            handleChange={handleChange}
            isRequired={isRequired}
        />
    );
};

export default renderMultiImageUpload;
